import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { BarChart } from '@mui/x-charts/BarChart';

import { marginReportService } from '../../services/marginReportService';
import type { MarginByProductItem } from '../../types/report';
import { ReportDateRangeControls } from './ReportDateRangeControls';
import { useReportDateRange } from './useReportDateRange';

export default function MarginByProductReportPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = Number(id);

  const { preset, from, to, setFrom, setTo, handlePresetChange } = useReportDateRange();

  const [marginByProduct, setMarginByProduct] = useState<MarginByProductItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) return;
    let isCancelled = false;

    setLoading(true);
    setError(null);

    marginReportService.getReportByProduct(companyId, from, to)
      .then(response => {
        if (!isCancelled) setMarginByProduct(response.products);
      })
      .catch(err => {
        console.error('Failed to load margin by-product report:', err);
        if (!isCancelled) setError('Не вдалося завантажити маржу по товарах');
      })
      .finally(() => {
        if (!isCancelled) setLoading(false);
      });

    return () => { isCancelled = true; };
  }, [companyId, from, to]);

  const marginProductLabels = useMemo(() => marginByProduct.map(p => p.productName), [marginByProduct]);
  const marginProductSeries = useMemo(() => marginByProduct.map(p => p.margin), [marginByProduct]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}/reports`)}
          size="small"
        >
          Назад
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Маржа по товарах
        </Typography>
      </Box>

      <ReportDateRangeControls
        preset={preset}
        onPresetChange={handlePresetChange}
        from={from}
        to={to}
        onFromChange={setFrom}
        onToChange={setTo}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : marginByProduct.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Немає даних за обраний період</Typography>
        </Paper>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <BarChart
              height={300}
              xAxis={[{ data: marginProductLabels, scaleType: 'band' }]}
              series={[{ data: marginProductSeries, label: 'Маржа', color: '#2e7d32' }]}
            />
          </Paper>

          <Paper sx={{ overflow: 'auto', maxHeight: 400 }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead" sx={{ position: 'sticky', top: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
                <Box component="tr">
                  {['Товар', 'Виручка', 'Собівартість', 'Маржа'].map(header => (
                    <Box
                      component="th"
                      key={header}
                      sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider', '&:first-of-type': { textAlign: 'left' } }}
                    >
                      {header}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {marginByProduct.map(product => (
                  <Box component="tr" key={product.productId}>
                    <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>{product.productName}</Box>
                    <Box component="td" sx={{ p: 1, textAlign: 'right', borderBottom: '1px solid', borderColor: 'divider' }}>${product.revenue.toFixed(2)}</Box>
                    <Box component="td" sx={{ p: 1, textAlign: 'right', borderBottom: '1px solid', borderColor: 'divider' }}>${product.cost.toFixed(2)}</Box>
                    <Box component="td" sx={{ p: 1, textAlign: 'right', borderBottom: '1px solid', borderColor: 'divider', fontWeight: 600, color: product.margin >= 0 ? 'success.main' : 'error.main' }}>
                      ${product.margin.toFixed(2)}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
}
